import { getLastBoardRequestTelemetry } from "./BoardRequestTelemetry.js";
import restFetchOptions, { getCurrentRestNonce } from "./restFetchOptions.js";

const BOARD_PERFORMANCE_ENDPOINT = "/wp-json/wagedwar/v1/board-performance";
const BOARD_LOAD_TIMEOUT_MS = 20000;
const BOARD_BOOTSTRAP_SETTLED_EVENT = "ww:board-bootstrap-settled";
const BOARD_MEASUREMENT_MAX_DEFER_MS = 10000;
const LCP_REPORT_DELAY_MS = 10000;

let lcpReporterInitialized = false;

const now = () => Date.now();

const getConnectionInfo = () => {
  if (typeof navigator === "undefined" || !navigator.connection) {
    return null;
  }

  return {
    effectiveType: navigator.connection.effectiveType || "",
  };
};

const getSessionId = () => {
  const storageKey = "ww_board_performance_session";

  try {
    const stored = sessionStorage.getItem(storageKey);
    if (stored) {
      return stored;
    }

    const generated = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(storageKey, generated);
    return generated;
  } catch (error) {
    return "";
  }
};

const sendMeasurement = (payload, preferBeacon = false) => {
  let body;
  try {
    body = JSON.stringify(payload);
  } catch (error) {
    return Promise.resolve(false);
  }

  if (
    preferBeacon &&
    !getCurrentRestNonce() &&
    typeof navigator !== "undefined" &&
    typeof navigator.sendBeacon === "function"
  ) {
    try {
      return Promise.resolve(navigator.sendBeacon(
        BOARD_PERFORMANCE_ENDPOINT,
        new Blob([body], { type: "application/json" })
      ));
    } catch (error) {
      // Fall through to keepalive fetch.
    }
  }

  // Member measurements need cookie authentication plus the REST nonce.
  // sendBeacon cannot supply that header, so members use keepalive fetch.
  return fetch(BOARD_PERFORMANCE_ENDPOINT, restFetchOptions({
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    cache: "no-store",
    keepalive: body.length < 60000,
  })).catch(() => false);
};

const sendMeasurementAfterBootstrap = (payload, preferBeacon = false) => {
  if (
    preferBeacon ||
    typeof window === "undefined" ||
    window.__WW_BOARD_BOOTSTRAP_PENDING !== true
  ) {
    return sendMeasurement(payload, preferBeacon);
  }

  let sent = false;
  let fallbackId;
  const send = () => {
    if (sent) {
      return;
    }

    sent = true;
    window.clearTimeout(fallbackId);
    window.removeEventListener(BOARD_BOOTSTRAP_SETTLED_EVENT, send);
    void sendMeasurement(payload);
  };

  window.addEventListener(BOARD_BOOTSTRAP_SETTLED_EVENT, send, { once: true });
  fallbackId = window.setTimeout(send, BOARD_MEASUREMENT_MAX_DEFER_MS);
  return Promise.resolve(true);
};

const initializeLcpReporter = () => {
  if (lcpReporterInitialized || typeof window === "undefined") {
    return;
  }
  lcpReporterInitialized = true;

  let reportTimer;
  const cleanup = () => {
    window.clearTimeout(reportTimer);
    window.removeEventListener("pagehide", handlePageHide);
    window.removeEventListener("pointerdown", handleInteraction, true);
    window.removeEventListener("keydown", handleInteraction, true);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };
  const report = (preferBeacon = false) => {
    const state = window.__WW_BOARD_LCP_STATE;
    const entry = state?.entry;
    if (!state?.supported || state.reported || !entry || !Number.isFinite(Number(entry.lcpMs))) {
      return false;
    }

    state.reported = true;
    cleanup();
    if (state.observer && typeof state.observer.disconnect === "function") {
      state.observer.disconnect();
    }
    const lcpMs = Math.max(0, Number(entry.lcpMs));
    void sendMeasurementAfterBootstrap({
      outcome: "success",
      loadType: "lcp",
      durationMs: lcpMs,
      athletesCount: 0,
      sport: "",
      stat: "",
      reason: "largest-contentful-paint",
      path: window.location.pathname,
      sessionId: getSessionId(),
      connection: getConnectionInfo(),
      stageTimings: { ...entry.stageTimings, lcpMs },
      lcpElement: entry.element || "unknown",
      lcpResourceHost: entry.resourceHost || "",
      lcpResourceType: entry.resourceType || "",
    }, preferBeacon);
    return true;
  };
  function handlePageHide() {
    report(true);
  }
  function handleVisibilityChange() {
    if (document.visibilityState === "hidden") {
      report(true);
    }
  }
  function handleInteraction() {
    window.setTimeout(() => report(false), 0);
  }
  const scheduleReport = () => {
    reportTimer = window.setTimeout(() => report(false), LCP_REPORT_DELAY_MS);
  };

  window.addEventListener("pagehide", handlePageHide, { once: true });
  window.addEventListener("pointerdown", handleInteraction, { capture: true, once: true });
  window.addEventListener("keydown", handleInteraction, { capture: true, once: true });
  document.addEventListener("visibilitychange", handleVisibilityChange);
  if (document.readyState === "complete") {
    scheduleReport();
  } else {
    window.addEventListener("load", scheduleReport, { once: true });
  }
};

export function createBoardPerformanceTracker(options = {}) {
  const prefetchedAt = Number(window.__WW_BOARD_BOOTSTRAP_PREFETCH?.startedAt);
  const pageStartedAt = Number(window.__WW_BOARD_PERFORMANCE_STARTED_AT);
  const explicitStartedAt = Number(options.startedAt);
  const loadType = options.loadType || "initial";
  if (loadType === "initial") {
    initializeLcpReporter();
  }
  const startedAt = Number.isFinite(explicitStartedAt)
    ? explicitStartedAt
    : Number.isFinite(prefetchedAt)
      ? prefetchedAt
      : Number.isFinite(pageStartedAt)
        ? pageStartedAt
        : now();
  const reactReadyAt = now();
  let completed = false;
  let latestContext = { ...(options.context || {}) };

  const finish = (outcome, context = {}) => {
    if (completed) {
      return false;
    }

    completed = true;
    window.clearTimeout(timeoutId);
    window.removeEventListener("pagehide", handlePageHide);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    latestContext = { ...latestContext, ...context };

    const durationMs = Math.max(0, now() - startedAt);
    const stageSource = loadType === "initial" && window.__WW_BOARD_STAGE_TIMINGS
      ? window.__WW_BOARD_STAGE_TIMINGS
      : null;
    const selectionStageTimings = latestContext.stageTimings && typeof latestContext.stageTimings === "object"
      ? latestContext.stageTimings
      : {};
    const stageTimings = stageSource
      ? {
        ...(Number.isFinite(Number(stageSource.topPlaysReadyAt))
          ? { topPlaysReadyMs: Math.max(0, Number(stageSource.topPlaysReadyAt) - startedAt) }
          : {}),
        ...(Number.isFinite(Number(stageSource.bootstrapReadyAt))
          ? { bootstrapReadyMs: Math.max(0, Number(stageSource.bootstrapReadyAt) - startedAt) }
          : {}),
        reactReadyMs: Math.max(0, reactReadyAt - startedAt),
        paintMs: durationMs,
        ...selectionStageTimings,
      }
      : selectionStageTimings;
    const requestTelemetry = getLastBoardRequestTelemetry();
    const includeRequestTelemetry = loadType !== "selection" || (
      Number(selectionStageTimings.selectionCacheHit) !== 1
      && Number(requestTelemetry.recordedAt) >= startedAt
    );
    const payload = {
      outcome,
      loadType,
      durationMs,
      athletesCount: Number(latestContext.athletesCount) || 0,
      sport: latestContext.sport || "",
      stat: latestContext.stat || "",
      reason: latestContext.reason || "",
      path: window.location.pathname,
      sessionId: getSessionId(),
      connection: getConnectionInfo(),
      stageTimings,
      ...(includeRequestTelemetry ? requestTelemetry : {}),
    };
    void sendMeasurementAfterBootstrap(payload, outcome === "abandoned");

    return true;
  };

  const update = (context = {}) => {
    latestContext = { ...latestContext, ...context };

    // Mobile browsers can suspend animation frames as soon as a tab or native
    // webview is backgrounded. If React already committed athlete data, record
    // that completed state instead of allowing a false load timeout.
    if (
      document.visibilityState === "hidden" &&
      Number(latestContext.athletesCount) > 0
    ) {
      finish("success", { reason: "athlete-data-rendered-before-hidden" });
    }
  };

  const handlePageHide = () => {
    finish("abandoned", { reason: "page-hidden-before-athletes-painted" });
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState !== "hidden") {
      return;
    }

    if (Number(latestContext.athletesCount) > 0) {
      finish("success", { reason: "athlete-data-rendered-before-hidden" });
      return;
    }

    finish("abandoned", { reason: "page-hidden-before-athletes-painted" });
  };

  const timeoutId = window.setTimeout(() => {
    finish("timeout", {
      reason: loadType === "selection"
        ? "selection-load-deadline"
        : "initial-board-load-deadline",
    });
  }, BOARD_LOAD_TIMEOUT_MS);

  window.addEventListener("pagehide", handlePageHide);
  document.addEventListener("visibilitychange", handleVisibilityChange);

  return {
    finish,
    update,
    dispose: () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    },
    isComplete: () => completed,
  };
}

export default createBoardPerformanceTracker;
